import { useTranslation } from "react-i18next";
import type {
  AssistantEvidenceCheck,
  ShownEvidenceCheck,
} from "../../../domains/gpt/evidenceCheck";
import type { GptSourceRef } from "../../../domains/gpt/transcript";
import { cn } from "../../../lib/utils";
import { SKELETON_PLACEHOLDER } from "../../ui/ChatSources";
import { EVIDENCE_CHIP_CLASS, VerifiedQuote } from "./EvidenceDetailsPanel";
import { ExplainedChip } from "./InfoPopover";

function sourceTitle(source: GptSourceRef) {
  switch (source.provenance) {
    case "juisci_internal":
    case "pubmed":
    case "web":
      return source.title;
    case "uploaded_document":
      return source.filename;
    default:
      throw new Error(source satisfies never);
  }
}

type CheckedClaim = Extract<
  AssistantEvidenceCheck,
  { status: "ready" }
>["claims"][number];

const CHECKED_STATUS_STYLE = {
  supported: "border-lime-200 bg-lime-50 text-lime-700",
  contested: "border-amber-200 bg-amber-50 text-amber-700",
  not_found: "border-orange-200 bg-orange-50 text-orange-700",
} as const satisfies Record<CheckedClaim["status"], string>;

function checkedStatusLabel(
  status: CheckedClaim["status"],
  t: (key: string) => string
): string {
  switch (status) {
    case "supported":
      return t("Supported");
    case "contested":
      return t("Contested");
    case "not_found":
      return t("Not found in its sources");
    default:
      throw new Error(status satisfies never);
  }
}

function checkedStatusExplanation(
  status: CheckedClaim["status"],
  t: (key: string) => string
): string {
  switch (status) {
    case "supported":
      return t(
        "A quote verified word-for-word in a cited source supports this statement."
      );
    case "contested":
      return t(
        "A quote verified word-for-word in a cited source contradicts this statement."
      );
    case "not_found":
      return t(
        "The sources this statement cites do not support it as written."
      );
    default:
      throw new Error(status satisfies never);
  }
}

function ClaimCard({
  claim,
  warning,
  sources,
  onOpenSource,
}: {
  claim: Pick<CheckedClaim, "status" | "text" | "edges">;
  // The numbers that no quote gives, null when the tab shows no warning.
  warning: string | null;
  sources: readonly GptSourceRef[];
  onOpenSource: (source: GptSourceRef, citationNumber: number) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-2.5 rounded-2xl border border-ds-slate-100 bg-white p-4">
      <div className="flex flex-wrap items-center gap-1.5">
        <ExplainedChip
          className={cn(
            EVIDENCE_CHIP_CLASS,
            "border",
            CHECKED_STATUS_STYLE[claim.status]
          )}
          label={checkedStatusLabel(claim.status, t)}
          title={checkedStatusLabel(claim.status, t)}
        >
          <span className="block">
            {checkedStatusExplanation(claim.status, t)}
          </span>
          <span className="mt-1 block">
            {t(
              "The status is computed automatically from the verified quotes, never chosen by the AI."
            )}
          </span>
        </ExplainedChip>
      </div>
      <p className="m-0 text-sm/snug font-semibold text-ds-black">
        {claim.text}
      </p>
      {warning !== null && (
        <p className="m-0 text-xs text-orange-700">{warning}</p>
      )}
      {claim.edges.map((edge, index) => {
        const source = sources.at(edge.sourceNumber - 1);
        if (source === undefined)
          throw new Error("should not happen: a quote cites no listed source");
        const title = sourceTitle(source);
        return (
          <VerifiedQuote
            key={index}
            relation={edge.relation}
            recommendationGrade={edge.recommendationGrade}
            verbatim={edge.verbatim}
            source={{
              number: edge.sourceNumber,
              title: title === null ? t("Untitled source") : title,
              onOpen: () => {
                onOpenSource(source, edge.sourceNumber);
              },
            }}
          />
        );
      })}
    </div>
  );
}

// The claim cards a running check holds the place of, as the Sources skeleton holds its rows.
const SKELETON_CLAIMS = [
  { chip: "w-20", textEnd: "w-2/3" },
  { chip: "w-24", textEnd: "w-1/2" },
  { chip: "w-20", textEnd: "w-3/4" },
] as const;

// The Evidence tab of a source answer: each statement checked against the sources that its block cites.
export function AssistantEvidenceCheckPanel({
  shown,
  sources,
  onOpenSource,
}: {
  shown: ShownEvidenceCheck;
  sources: readonly GptSourceRef[];
  onOpenSource: (source: GptSourceRef, citationNumber: number) => void;
}) {
  const { t } = useTranslation();
  if (shown.scope === "pending")
    return (
      <div className="flex flex-col gap-4">
        <p className="m-0 text-sm text-ds-slate-600">
          {t(
            "Checking this answer against its sources. This takes less than a minute."
          )}
        </p>
        <div
          role="status"
          aria-label={t("Loading…")}
          className="flex flex-col gap-4"
        >
          {SKELETON_CLAIMS.map((claim, index) => (
            <div
              key={index}
              aria-hidden
              className="flex flex-col gap-2.5 rounded-2xl border border-ds-slate-100 bg-white p-4"
            >
              <span
                className={cn(
                  SKELETON_PLACEHOLDER,
                  "h-5.5 rounded-full",
                  claim.chip
                )}
              />
              <span className="flex flex-col gap-1.5">
                <span
                  className={cn(SKELETON_PLACEHOLDER, "h-3.5 rounded-full")}
                />
                <span
                  className={cn(
                    SKELETON_PLACEHOLDER,
                    "h-3.5 rounded-full",
                    claim.textEnd
                  )}
                />
              </span>
              <span className="flex flex-col gap-1.5 border-l-2 border-ds-slate-100 pl-3">
                <span
                  className={cn(
                    SKELETON_PLACEHOLDER,
                    "h-3 w-full rounded-full"
                  )}
                />
                <span
                  className={cn(SKELETON_PLACEHOLDER, "h-3 w-1/2 rounded-full")}
                />
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  const intro = (
    <p className="m-0 text-sm text-ds-slate-600">
      {t("Each statement is checked against the sources it cites.")}
    </p>
  );
  if (shown.scope === "verified")
    return (
      <div className="flex flex-col gap-4">
        {intro}
        {shown.claims.map((claim) => (
          <ClaimCard
            key={claim.id}
            claim={claim}
            warning={null}
            sources={sources}
            onOpenSource={onOpenSource}
          />
        ))}
      </div>
    );
  const { check } = shown;
  switch (check.status) {
    case "failed":
      return (
        <p className="m-0 text-sm text-ds-slate-600">
          {t("The evidence check failed. The answer itself is unchanged.")}
        </p>
      );
    case "no_claims":
      return (
        <p className="m-0 text-sm text-ds-slate-600">
          {t("No verifiable statement in this answer.")}
        </p>
      );
    case "ready":
      return (
        <div className="flex flex-col gap-4">
          {intro}
          {check.claims.map((claim) => (
            <ClaimCard
              key={claim.id}
              claim={claim}
              warning={
                claim.ungroundedNumbers.length === 0
                  ? null
                  : t("Numbers not found in its cited sources: {{numbers}}", {
                      numbers: claim.ungroundedNumbers.join(", "),
                    })
              }
              sources={sources}
              onOpenSource={onOpenSource}
            />
          ))}
          {check.droppedClaimCount > 0 && (
            <p className="m-0 text-xs text-ds-slate-500">
              {t("{{count}} statements were left out of the check.", {
                count: check.droppedClaimCount,
              })}
            </p>
          )}
        </div>
      );
    default:
      throw new Error(check satisfies never);
  }
}
